import React, { useEffect } from 'react';
import classnames from 'classnames';
import PropTypes from 'prop-types';
import { useMediaQuery } from 'react-responsive';

import {
  MEDIA_QUERY_MEDIUM_SCREEN_750,
  MEDIA_QUERY_LARGE_SCREEN,
} from 'client/constants/mediaQueries';

import getUserNameInitials from 'common/util/getUserNameInitials';

import './SrPartnerPageV2.less';
import SrBox from 'client/components/SrBox';
import SrText from 'client/components/SrText';
import SrLink from 'client/components/SrLink/SrLink';
import SrFlex from 'client/components/SrFlex';
import SrViewAsPartnerToggle from 'client/components/SrViewAsPartnerToggle';
import SrProgressBarPercent, {
  SR_PROGRESS_BAR_THEME_YELLOW,
} from 'client/components/SrProgressBarPercent/SrProgressBarPercent';

export const SR_PARTNER_PAGE_THEME_BLANK_STATE = 'SR_PARTNER_PAGE_THEME_BLANK_STATE';
export const SR_PARTNER_PAGE_THEME_TO_CLASSNAME_MAPPING = {
  [SR_PARTNER_PAGE_THEME_BLANK_STATE]: 'srPartnerPageThemeBlankState',
};
export const SR_PARTNER_PAGE_THEMES = Object.keys(SR_PARTNER_PAGE_THEME_TO_CLASSNAME_MAPPING);

export const SR_PARTNER_PAGE_V2_FONT_SIZE_SMALL = 'SR_PARTNER_PAGE_V2_FONT_SIZE_SMALL';
export const SR_PARTNER_PAGE_V2_FONT_SIZE_MEDIUM = 'SR_PARTNER_PAGE_V2_FONT_SIZE_MEDIUM';
export const SR_PARTNER_PAGE_V2_FONT_SIZE_TO_CLASSNAME_MAPPING = {
  [SR_PARTNER_PAGE_V2_FONT_SIZE_SMALL]: 'font-size-small',
  [SR_PARTNER_PAGE_V2_FONT_SIZE_MEDIUM]: 'font-size-medium',
};
export const SR_PARTNER_PAGE_V2_FONT_SIZES = Object.keys(SR_PARTNER_PAGE_V2_FONT_SIZE_TO_CLASSNAME_MAPPING);

const SrPartnerPageV2 = ({
  className,
  children,
  backUrl,
  onBack,
  navbarImage,
  navbarTitle,
  pageHeader,
  headerSize,
  progressBarPercent,
  progressBarText,
  theme,
  step,
  shouldScrollToTop,
  padding,
  contentPadding,
  maxWidth,
  navbarMaxWidth,
  pageSubtitle,
  hasWatermark,
  currentUser,
  currentPartner,
  redirectToLastLocation,
}) => {
  const isLargeScreen = useMediaQuery({
    query: MEDIA_QUERY_LARGE_SCREEN,
  });

  const isMediumScreen = useMediaQuery({
    query: MEDIA_QUERY_MEDIUM_SCREEN_750,
  });

  useEffect(() => {
    if (shouldScrollToTop) {
      if (process.browser) {
        global.scrollTo(0, 0);
      }
    }
  }, [step, shouldScrollToTop]);
  return (
    <div
      className={className}
      styleName={classnames(
        'SrPartnerPageV2',
        SR_PARTNER_PAGE_THEME_TO_CLASSNAME_MAPPING[theme],
        { hasProgressBar: Boolean(progressBarPercent), hasWatermark }
      )}
      style={{ padding }}
    >
      <div className='SrPartnerPageV2-navBar' styleName='navBar'>
        <div
          className='SrPartnerPageV2-navBarContent'
          styleName='navBarContent'
          style={{ maxWidth: navbarMaxWidth }}
        >
          {onBack && (
            <div styleName='backArrow' onClick={onBack} />
          )}
          {!onBack && backUrl && (
            <SrLink
              to={backUrl}
              redirectToLastLocation={redirectToLastLocation}
              restoreScrollPosition
            >
              <div styleName='backArrow' />
            </SrLink>
          )}
          {!onBack && !backUrl && <SrBox width='40px' marginLeft='15px' />}
          {navbarImage &&
            <SrBox
              backgroundImage={`url(${navbarImage})`}
              backgroundSize='contain'
              backgroundRepeat='no-repeat'
              backgroundPosition='center'
              height='44px'
              width='100%'
            />
          }
          {navbarTitle && !navbarImage &&
            <SrBox
              maxWidth={isMediumScreen
                ? 'calc(100vw - 150px)'
                : `calc(100vw - (100vw - ${maxWidth}) - 150px)`
              }
            >
              <span styleName='navbarTitle'>
                {navbarTitle}
              </span>
            </SrBox>
          }
          {currentUser ? (
            <SrBox>
              <div styleName='SrPartnerPageNavbarDropdown'>
                <div styleName='userContainer'>
                  <span styleName='userName'>{currentUser.name}</span>
                  <span styleName='userRoles'>
                    Account Settings
                  </span>
                </div>
                <div styleName='userNameCircle'>
                  <span>{getUserNameInitials(currentUser.name)}</span>
                </div>

                <div styleName='menu'>
                  {currentPartner?.slug && (
                    <SrLink to={`/partner/${currentPartner.slug}/settings`}>
                      <span styleName='option danger first'>
                        <span>Settings</span>
                      </span>
                    </SrLink>
                  )}
                  <SrLink to='/logout'>
                    <span styleName='option danger'>
                      <span>Logout</span>
                    </span>
                  </SrLink>
                </div>
                <SrBox position='absolute' left='-100px' top='-4px' height='40px'>
                  <SrViewAsPartnerToggle partnerId={currentPartner?.id} />
                </SrBox>
              </div>
            </SrBox>
          ) : <SrBox width='40px' marginLeft='15px' />}
        </div>
      </div>
      {
        progressBarPercent &&
        <SrProgressBarPercent
          percent={progressBarPercent}
          text={progressBarText}
          theme={SR_PROGRESS_BAR_THEME_YELLOW}
        />
      }

      <SrBox
        maxWidth={maxWidth}
        margin='0 auto'
        padding={contentPadding || '0 10px'}
      >
        {pageHeader && (
          <SrBox
            margin={isLargeScreen ? '0 15px 33px 15px' : '0 0 21px 0'}
          >
            <div
              styleName={classnames(
                'pageHeader',
                { hasProgressBar: Boolean(progressBarPercent) },
                SR_PARTNER_PAGE_V2_FONT_SIZE_TO_CLASSNAME_MAPPING[headerSize]
              )}
            >
              {pageHeader}
            </div>
          </SrBox>
        )}
        {pageSubtitle && (
          <SrBox margin={isLargeScreen ? '-19px 15px 25px 15px' : '0 0 27px 0'}>
            <SrText
              font='greycliff'
              fontSize='16px'
              lineHeight='25px'
              color='blueGreycliff'
              opacity={typeof pageSubtitle === 'string' ? 0.6 : null}
              weight='500'
              letterSpacing='0px'
            >
              {pageSubtitle}
            </SrText>
          </SrBox>
        )}
        {children}
      </SrBox>
      {hasWatermark &&
        <SrBox margin={isLargeScreen ? '100px auto 0' : '200px auto 0'}>
          <SrFlex justify='center'>
            <SrBox
              width='160px'
              height='100px'
              backgroundImage='url(/images/sunroom-watermark-grey.svg)'
              backgroundPosition='center top'
              backgroundSize='contain'
              backgroundRepeat='no-repeat'
            />
          </SrFlex>
        </SrBox>
      }
    </div>
  );
};

SrPartnerPageV2.propTypes = {
  className: PropTypes.string,
  children: PropTypes.node,
  backUrl: PropTypes.string,
  onBack: PropTypes.func,
  navbarImage: PropTypes.string,
  navbarTitle: PropTypes.string,
  pageHeader: PropTypes.string,
  pageSubtitle: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.node,
  ]),
  progressBarPercent: PropTypes.number,
  progressBarText: PropTypes.string,
  theme: PropTypes.oneOf(SR_PARTNER_PAGE_THEMES),
  step: PropTypes.number,
  shouldScrollToTop: PropTypes.bool,
  padding: PropTypes.string,
  contentPadding: PropTypes.string,
  maxWidth: PropTypes.string,
  navbarMaxWidth: PropTypes.string,
  headerSize: PropTypes.oneOf(SR_PARTNER_PAGE_V2_FONT_SIZES),
  hasWatermark: PropTypes.bool,
  currentUser: PropTypes.object,
  currentPartner: PropTypes.object,
  redirectToLastLocation: PropTypes.bool,
};

SrPartnerPageV2.defaultProps = {
  maxWidth: '895px',
  navbarMaxWidth: '895px',
  headerSize: SR_PARTNER_PAGE_V2_FONT_SIZE_MEDIUM,
  hasWatermark: false,
  redirectToLastLocation: true,
};

SrPartnerPageV2.displayName = 'SrPartnerPageV2';

export default SrPartnerPageV2;
